import type { ComponentType, FC, ReactNode, ComponentProps, RefObject } from "react";
import type { GridColDef, GridRowSelectionModel, GridSortModel, GridApiPremium } from "@mui/x-data-grid-premium";
import {
  gridClasses,
  gridFilteredSortedRowIdsSelector,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { createContext, use, useLayoutEffect, useMemo, useState } from "react";
import { PAGE_PADDING_X, PAGE_PADDING_Y, PageSpinner, PageWrapper } from "../shared/Page";
import type { Answer, Form, QuestionCard } from "../dynamicForm";
import { DynamicForm } from "../dynamicForm";
import type { GridProps } from "../shared/Grid";
import { Grid } from "../shared/Grid";
import type { DrawerProps } from "../shared/Drawer";
import { Drawer } from "../shared/Drawer";
import { multilineEllipsis } from "../shared/multilineEllipsis";
import { ArchivedIcon2, type IconWithBackgroundProps } from "../shared/icons";
import { requiredColDef } from "./misc";
import type { SavedFiltersProps } from "./SavedFilters";
import type { AsyncQuestionCardData } from "./columnDefinitions";
import { getQuestionCardToColDef } from "./columnDefinitions";
import { SidePanel, type SidePanelProps } from "./SidePanels";
import { useOutlet, useParams, type RouteObject } from "react-router";
import { parseParams } from "../shared/useParsedParams";
import { z } from "zod";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import { useGetActiveToolbarTabValue, useShowArchived } from "./Toolbar";
import { OptionFilterFromSearchParams } from "./optionFilterFromSearchParams";

const ROW_ID_PARAM = "rowId";
const ACTIONS_COLUMN_CLASS_NAME = "actions-column";

type F = Form | undefined | "no-form";

type ArchivedAt = string | undefined;

type RowData<FormProp extends F, ExtraFields> = { archivedAt?: ArchivedAt } & (FormProp extends "no-form"
  ? NonNullable<ExtraFields>
  : ExtraFields & { answers: Answer[] });

type DynamicGridTexts = { searchFieldPlaceholder?: string };

export type ToolbarTabs<RowData, ToolbarTabValue extends string = string> = {
  queryKey: string;
  filter: (rowData: RowData, activeTab: ToolbarTabValue) => boolean;
  tabs: { labelKey: TranslationKey; value: ToolbarTabValue }[];
  sizeToText?: boolean;
};

type DynamicGridProps<FormProp extends F, ExtraFields, ToolbarTabValue extends string = string> = {
  /** "undefined" will show the loading state */
  rowsData: RowData<FormProp, ExtraFields>[] | undefined;
  sidePanelProps?: SidePanelProps;
  getRowId: (rowData: RowData<FormProp, ExtraFields>) => string;
  savedFiltersProps?: SavedFiltersProps;
  mainColumnProps: MainColumnProps<ExtraFields>;
  getColumns: (questionCardColumns: GridColDef[]) => GridColDef[];
  Toolbar: ComponentType;
  Footer: ComponentType;
  NoRowsOverlay: ComponentType;
  onRowSelect: (id: string) => void;
  texts?: DynamicGridTexts;
  defaultSort?: GridSortModel[number];
  partsSx?: GridProps["partsSx"];
  toolbarTabs?: ToolbarTabs<RowData<FormProp, ExtraFields>, ToolbarTabValue>;
  showArchivedFilter?: boolean;
  renderActionMenu?: (props: { selectedRowId: string; row: RowData<FormProp, ExtraFields> }) => ReactNode;
  /** "undefined" will show the loading state. */
  form: FormProp;
  /** Only used when question cards are present. */
  asyncQuestionCardData?: FormProp extends "no-form" ? undefined : AsyncQuestionCardData;

  /** Extra vertical offset (in px) to subtract when the page has content above the grid; it should match that content's total height. */
  extraHeightOffset?: number;
};

/** HACK: This should be updated manually if the toolbars height changes. */
const TOOLBAR_HEIGHT = "145px";

export const HEIGHT = (extraHeightOffset = 0) =>
  `calc(100vh - ${PAGE_PADDING_Y} - ${TOOLBAR_HEIGHT} - ${extraHeightOffset}px)`;

export const DynamicGrid = <FormProp extends F, ExtraFields, ToolbarTabValue extends string = string>({
  rowsData,
  form,
  asyncQuestionCardData,
  getRowId,
  mainColumnProps,
  getColumns,
  onRowSelect,
  defaultSort,
  Toolbar,
  Footer,
  NoRowsOverlay,
  texts,
  savedFiltersProps,
  sidePanelProps,
  partsSx,
  renderActionMenu,
  toolbarTabs,
  showArchivedFilter,
  extraHeightOffset,
}: DynamicGridProps<FormProp, ExtraFields, ToolbarTabValue>) => {
  const { t } = useTranslation();
  const selectedRowId = useOptionalSelectedRowId();
  const activeDrawer = useActiveDrawerElement();
  const isLoading = !form || !rowsData;
  const filterByActiveTabValue = useGetFilterByActiveTabValue(toolbarTabs);
  const { showArchived } = useShowArchived();
  const gridApiRef = useGridApiRef();
  const [panelTarget, setPanelTarget] = useState<HTMLButtonElement | null>(null);

  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({ type: "include", ids: selectedRowId ? new Set([selectedRowId]) : new Set() }),
    [selectedRowId]
  );

  const rows = useMemo(() => {
    if (!rowsData) return [];

    const filteredRowsData = filterByActiveTabValue(
      showArchivedFilter && !showArchived ? rowsData.filter(rowData => !rowData.archivedAt) : rowsData
    );

    return filteredRowsData.map(rowData => {
      if ("answers" in rowData) {
        const { answers, ...extraFields } = rowData;
        return {
          id: getRowId(rowData),
          ...extraFields,
          ...Object.fromEntries(answers.map(({ value, questionId }) => [questionId, value])),
        };
      }
      return { id: getRowId(rowData), ...rowData };
    });
  }, [rowsData, getRowId, filterByActiveTabValue, showArchivedFilter, showArchived]);

  const actionColumn: GridColDef<RowData<FormProp, ExtraFields>> | undefined = useMemo(
    () =>
      renderActionMenu
        ? {
            field: Grid.ACTIONS_COLUMN_FIELD,
            type: "actions",
            disableColumnMenu: true,
            headerName: "",
            cellClassName: ACTIONS_COLUMN_CLASS_NAME,
            width: 80,
            renderCell: ({ row }) => renderActionMenu({ selectedRowId: getRowId(row), row }),
          }
        : undefined,
    [renderActionMenu, getRowId]
  );

  const questionCardColumns = useMemo(
    () =>
      form === "no-form"
        ? []
        : mapFormToColumns(form, questionCard =>
            getQuestionCardToColDef({ questionCard, asyncData: asyncQuestionCardData ?? {}, t })
          ),
    [form, asyncQuestionCardData, t]
  );

  const columns = useMemo(
    () => [mainColumn(mainColumnProps), ...getColumns(questionCardColumns), ...(actionColumn ? [actionColumn] : [])],
    [mainColumnProps, getColumns, questionCardColumns, actionColumn]
  );

  return (
    <PageWrapper sx={{ width: activeDrawer ? `calc(100% - ${DRAWER_WIDTH})` : "100%", px: 0, pb: 0 }}>
      {isLoading ? (
        <PageSpinner />
      ) : (
        <GridContext
          value={{
            gridApiRef,
            savedFiltersProps,
            texts,
            sidePanelProps,
            selectedRowId,
            setPanelTarget,
            toolbarTabsProps: toolbarTabs
              ? { queryKey: toolbarTabs.queryKey, tabs: toolbarTabs.tabs, sizeToText: toolbarTabs.sizeToText }
              : undefined,
            showArchivedFilter,
          }}
        >
          <Stack>
            <Toolbar />
            {form && form !== "no-form" && <OptionFilterFromSearchParams form={form} />}

            <Stack
              direction="row"
              sx={{ mr: activeDrawer ? "-2px" /** To make to drawer overlap the grid a TINY bit */ : 0 }}
            >
              <Grid
                initialState={{
                  sorting: { sortModel: [defaultSort ?? { field: MAIN_FIELD, sort: "asc" }] },
                  pinnedColumns: { left: [MAIN_FIELD], right: actionColumn ? [Grid.ACTIONS_COLUMN_FIELD] : [] },
                }}
                apiRef={gridApiRef}
                /** Todo: Make ColumnResize work with saved filters */
                disableColumnResize
                /** Todo: Make ColumnReorder work with saved filters */
                disableColumnReorder
                getRowHeight={() => "auto"}
                disableMultipleRowSelection
                disableRowSelectionExcludeModel
                onRowClick={params => onRowSelect(String(params.id))}
                rowSelectionModel={rowSelectionModel}
                slotProps={{ panel: { target: panelTarget } }}
                slots={{
                  footer: Footer,
                  noRowsOverlay: NoRowsOverlay,
                  noResultsOverlay: NoRowsOverlay,
                }}
                rows={rows}
                columns={columns}
                sortingOrder={["asc", "desc"]}
                sx={{
                  height: HEIGHT(extraHeightOffset),
                  /** Hide the shadow for the right pinned column (aka action column) */ [`& .${gridClasses["scrollShadow"]}`]:
                    { "--opacity": "0.09", "--hasScrollEnd": "0 !important" },
                }}
                partsSx={{
                  cellSx: { "alignItems": "flex-start", "p": 2, "&:first-child": { pl: PAGE_PADDING_X } },
                  rowSx: ACTION_MENU_ROW_SX,
                  columnHeaderSx: { "lineHeight": "normal", "&:first-child": { pl: PAGE_PADDING_X } },
                  ...partsSx,
                }}
              />

              {!selectedRowId && sidePanelProps && (
                <WithInitialisedGridApiRef>
                  <Box sx={{ height: HEIGHT(extraHeightOffset), pb: 4 /*Hardcoded to match the gridfooter height*/ }}>
                    <SidePanel {...sidePanelProps} />
                  </Box>
                </WithInitialisedGridApiRef>
              )}
            </Stack>
          </Stack>
        </GridContext>
      )}
      {activeDrawer}
    </PageWrapper>
  );
};

type GridContextValue = {
  gridApiRef: RefObject<GridApiPremium | null>;
  texts?: DynamicGridTexts;
  selectedRowId?: string;
  savedFiltersProps?: SavedFiltersProps;
  toolbarTabsProps?: { queryKey: string; tabs: { labelKey: TranslationKey; value: string }[]; sizeToText?: boolean };
  sidePanelProps?: SidePanelProps;
  showArchivedFilter?: boolean;

  setPanelTarget?: (element: HTMLButtonElement | null) => void;
};
export const GridContext = createContext<GridContextValue | undefined>(undefined);

export const useGridContext = () => {
  const context = use(GridContext);
  if (!context) throw new Error("GridContext must be used within a GridContextProvider");
  return context;
};

export const MAIN_FIELD = "name";

type MainColumnProps<ExtraFields> = {
  headerName: string;
  renderLabel: (row: ExtraFields) => string;
  renderIcon: (props: { iconProps: IconWithBackgroundProps; row: ExtraFields }) => ReactNode;
};

const MAIN_COLUMN_PADDING_TOP = "4px";
export const MAIN_COLUMN_WIDTH = 280;
const mainColumn = <ExtraFields,>({
  headerName,
  renderLabel,
  renderIcon,
}: MainColumnProps<ExtraFields>): GridColDef<RowData<"no-form", ExtraFields>> => ({
  ...requiredColDef({ headerName, field: MAIN_FIELD }),
  width: MAIN_COLUMN_WIDTH,
  valueGetter: (_, row) => renderLabel(row),
  renderCell: ({ row }) => {
    return (
      <Stack direction="row" sx={{ mt: "-1px" }}>
        <Box>{row.archivedAt ? <ArchivedIcon2 size="sm" /> : renderIcon({ iconProps: { size: "sm" }, row })}</Box>
        <Stack sx={{ ml: 2 }}>
          <Typography
            variant="body2"
            sx={{
              pt: MAIN_COLUMN_PADDING_TOP,
              lineHeight: 1.2,
              fontWeight: "600",
              overflowWrap: "break-all",
              ...multilineEllipsis(2),
              textWrap: "balance",
            }}
          >
            {renderLabel(row)}
          </Typography>
          {row.archivedAt && <ArchivedSubLabel />}
        </Stack>
      </Stack>
    );
  },
});

const ArchivedSubLabel = () => {
  const { t } = useTranslation();
  return (
    <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), color: theme => theme.palette.text.tertiary }}>
      {t("DynamicFormGrid.MainColumn.Archived")}
    </Typography>
  );
};

const mapFormToColumns = (
  form: Form | undefined,
  questionCardToColDef: (questionCard: QuestionCard) => GridColDef | null
): GridColDef[] => {
  if (!form) return [];

  const columns: GridColDef[] = [];

  /** Go through all question cards including follow-ups in all sections */
  form.sections.forEach(({ questionCards }) =>
    questionCards.forEach(rootQuestionCard => {
      DynamicForm.traverseQuestionCard(rootQuestionCard, {
        onVisit: questionCard => {
          const colDef = questionCardToColDef(questionCard);
          if (colDef) columns.push(colDef);
        },
      });
    })
  );

  return columns;
};

const hasExpandedActionMenu = `&:has(.${ACTIONS_COLUMN_CLASS_NAME} [aria-expanded="true"])`;
const isRowHovering = `&:hover .${ACTIONS_COLUMN_CLASS_NAME}`;
const isRowFocused = `&:has(:focus-visible) .${ACTIONS_COLUMN_CLASS_NAME}`;
const ACTION_MENU_ROW_SX = {
  [`& .${ACTIONS_COLUMN_CLASS_NAME}`]: { opacity: 0, pr: 5 },
  [`${isRowHovering}, ${isRowFocused}, ${hasExpandedActionMenu} .${ACTIONS_COLUMN_CLASS_NAME}`]: { opacity: 1 },
  [`${hasExpandedActionMenu}`]: { backgroundColor: (theme: Theme) => theme.palette.action.hover },
} satisfies SxProps<Theme>;

type DynamicGridFooterProps = {
  numberOfRowsTextKey: ComponentProps<typeof Grid.NumberOfRows>["textKey"];
};
export const DynamicGridFooter: FC<DynamicGridFooterProps> = ({ numberOfRowsTextKey }) => {
  return (
    <Grid.Footer>
      <Grid.NumberOfRows textKey={numberOfRowsTextKey} />
    </Grid.Footer>
  );
};

export const DRAWER_WIDTH = "clamp(760px,50vw,900px)";

export const DynamicGridDrawer = (props: DrawerProps) => {
  return <Drawer {...props} width={props.width ?? DRAWER_WIDTH} />;
};

export const WithInitialisedGridApiRef: FC<{
  children: ReactNode | ((gridApiRef: RefObject<GridApiPremium>) => ReactNode);
}> = ({ children }) => {
  const gridContext = useGridContext();
  const [initialisedGridApiRef, setInitialisedGridApiRef] = useState<RefObject<GridApiPremium> | null>(null);

  useLayoutEffect(() => {
    const { gridApiRef } = gridContext;
    if (gridApiRef.current) setInitialisedGridApiRef(gridApiRef as RefObject<GridApiPremium>);
  }, [gridContext]);

  if (!initialisedGridApiRef?.current) return null;

  return typeof children === "function" ? children(initialisedGridApiRef) : children;
};

export const useInitialisedGridApiRef = () => {
  const gridContext = useGridContext();
  if (!gridContext.gridApiRef.current) {
    throw new Error("gridApiRef needs to be initialised. Try wrapping the component in WithInitialisedGridApiRef");
  }
  return gridContext.gridApiRef as RefObject<GridApiPremium>;
};

export const useGetFilteredRowsData = <RowData,>(
  rowsData: RowData[] | undefined,
  getRowId: (rowData: RowData) => string
) => {
  const gridApiRef = useInitialisedGridApiRef();
  const filteredRowIds = useGridSelector(gridApiRef, gridFilteredSortedRowIdsSelector);

  if (!rowsData) return [];

  return rowsData.filter(row => filteredRowIds.includes(getRowId(row)));
};

export const setupRoutes = ({
  gridElement,
  drawerRoutes,
  rowDrawerRoutes,
  gridPrefetcher,
  rowDrawerDefaultPrefetcher,
}: {
  gridElement: ReactNode;
  drawerRoutes?: RouteObject[];
  rowDrawerRoutes: RouteObject[];
  gridPrefetcher?: () => void;
  rowDrawerDefaultPrefetcher?: (params: { [ROW_ID_PARAM]: string }) => void;
}): RouteObject[] => [
  { path: "", element: gridElement, loader: () => void gridPrefetcher?.(), children: drawerRoutes },
  {
    path: `:${ROW_ID_PARAM}`,
    element: gridElement,
    children: rowDrawerRoutes,
    loader: ({ params }) => {
      gridPrefetcher?.();
      return rowDrawerDefaultPrefetcher?.(parseParams(params, z.object({ [ROW_ID_PARAM]: z.string() })));
    },
  },
];

/**  Returns the drawer element associated with the currently matched drawer route.*/
export const useActiveDrawerElement = () => useOutlet();

export const useSelectedRowId = () => {
  const params = useParams();
  return selectedRowIdFromParams(params);
};

export const selectedRowIdFromParams = (params: unknown) =>
  parseParams(params, z.object({ [ROW_ID_PARAM]: z.string() }), "Must be used within a DynamicGrid.setupRoutes()")[
    ROW_ID_PARAM
  ];

export const useOptionalSelectedRowId = () => {
  const params = useParams();
  return params[ROW_ID_PARAM];
};

export const useGetFilterByActiveTabValue = <RowData, TabValue extends string = string>(
  toolbarTabs?: ToolbarTabs<RowData, TabValue>
) => {
  const toolbarTabUtils = useGetActiveToolbarTabValue<TabValue>();

  return (rowsData: Array<RowData>) => {
    if (!toolbarTabs) return rowsData;
    const activeTabValue = toolbarTabUtils.getActiveTabValue(toolbarTabs.queryKey, toolbarTabs.tabs);
    return rowsData.filter(rowData => toolbarTabs.filter(rowData, activeTabValue));
  };
};
